import * as React from 'react';
import {
  Modal, Flex, FlexItem, Text, Button, ButtonVariant,
  TextInput, Form, Grid, GridItem,
} from '@patternfly/react-core';
import TypeaheadSelectInput from 'ui/components/TypeaheadSelectInput';
import { RosterState } from 'domain/RosterState';
import { tenantOperations } from 'store/tenant';
import { connect } from 'react-redux';
import { AppState } from 'store/types';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

interface StateProps {
  timezoneList: string[];
}

const mapStateToProps = (state: AppState, props: OwnProps) => ({
  ...props,
  timezoneList: state.tenantData.timezoneList,
});

interface DispatchProps {
  addTenant: typeof tenantOperations.addTenant;
  refreshSupportedTimezones: typeof tenantOperations.refreshSupportedTimezones;
}

const mapDispatchToProps: DispatchProps = {
  addTenant: tenantOperations.addTenant,
  refreshSupportedTimezones: tenantOperations.refreshSupportedTimezones,
};

interface OwnProps {
  isOpen: boolean;
  onClose: () => void;
}

export type Props = StateProps & DispatchProps & OwnProps;

export function isFormCompleted(rs: Partial<RosterState>): rs is RosterState {
  return rs.draftLength !== undefined && rs.firstDraftDate !== undefined
    && rs.lastHistoricDate !== undefined && rs.publishLength !== undefined
    && rs.publishNotice !== undefined && rs.rotationLength !== undefined
    && rs.rotationLength >= 2 && rs.tenant !== undefined && rs.timeZone !== undefined
    && rs.unplannedRotationOffset !== undefined;
}

export function NewTenantFormModal(props: Props) {
  const { t } = useTranslation('NewTenantFormModal');
  const { refreshSupportedTimezones } = props;
  React.useEffect(() => {
    refreshSupportedTimezones();
  }, [refreshSupportedTimezones]);

  const [formData, setFormData] = React.useState<Partial<RosterState>>({
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    publishLength: 7,
    unplannedRotationOffset: 0,
  });

  const setProperty = (properties: Partial<RosterState>) => {
    setFormData({ ...formData, ...properties });
  };

  return (
    <Modal
      title={t('createTenant')}
      onClose={props.onClose}
      isOpen={props.isOpen}
      actions={
        [(
          <Button
            aria-label="Close Modal"
            variant={ButtonVariant.tertiary}
            key={0}
            onClick={props.onClose}
          >
            {t('close')}
          </Button>
        ),
        (
          <Button
            isDisabled={!isFormCompleted(formData)}
            aria-label="Save"
            data-cy="save"
            key={2}
            onClick={() => {
              if (isFormCompleted(formData)) {
                props.addTenant(formData);
                props.onClose();
              }
            }}
          >
            {t('save')}
          </Button>
        ),
        ]
      }
      variant="small"
    >
      <Form onSubmit={(e) => e.preventDefault()}>
        <Flex direction={{ default: 'column' }} spaceItems={{ default: 'spaceItemsMd' }}>
          <FlexItem>
            <Grid>
              <GridItem span={6}>
                <Text>{t('name')}</Text>
              </GridItem>
              <GridItem span={6}>
                <TextInput
                  aria-label="Name"
                  data-cy="name"
                  onChange={(name) => setProperty({
                    tenant: {
                      name,
                    },
                  })}
                />
              </GridItem>
            </Grid>
          </FlexItem>
          <FlexItem>
            <Grid>
              <GridItem span={6}>
                <Text>{t('scheduleStartDate')}</Text>
              </GridItem>
              <GridItem span={6}>
                <TextInput
                  type="date"
                  aria-label="Schedule Start Date"
                  data-cy="schedule-start-date"
                  onChange={(date) => setProperty({
                    lastHistoricDate: moment(date).subtract(1, 'day').toDate(),
                    firstDraftDate: moment(date).toDate(),
                  })}
                />
              </GridItem>
            </Grid>
          </FlexItem>
          <FlexItem>
            <Grid>
              <GridItem span={6}>
                <Text>{t('draftLength')}</Text>
              </GridItem>
              <GridItem span={6}>
                <TextInput
                  type="number"
                  aria-label="Draft Length"
                  data-cy="draft-length"
                  onChange={(length) => setProperty({ draftLength: parseInt(length, 10) })}
                />
              </GridItem>
            </Grid>
          </FlexItem>
          <FlexItem>
            <Grid>
              <GridItem span={6}>
                <Text>{t('publishNotice')}</Text>
              </GridItem>
              <GridItem span={6}>
                <TextInput
                  type="number"
                  aria-label="Publish Notice"
                  data-cy="publish-notice"
                  onChange={(notice) => setProperty({ publishNotice: parseInt(notice, 10) })}
                />
              </GridItem>
            </Grid>
          </FlexItem>
          <FlexItem>
            <Grid>
              <GridItem span={6}>
                <Text>{t('publishLength')}</Text>
              </GridItem>
              <GridItem span={6}>
                <TextInput
                  defaultValue="7"
                  type="number"
                  onChange={(length) => setProperty({ publishLength: parseInt(length, 10) })}
                  aria-label="Publish Length"
                  data-cy="publish-length"
                  isDisabled
                />
              </GridItem>
            </Grid>
          </FlexItem>
          <FlexItem>
            <Grid>
              <GridItem span={6}>
                <Text>{t('rotationLength')}</Text>
              </GridItem>
              <GridItem span={6}>
                <TextInput
                  type="number"
                  aria-label="Rotation Length"
                  onChange={(length) => setProperty({ rotationLength: parseInt(length, 10) })}
                  data-cy="rotation-length"
                  min={2}
                />
              </GridItem>
            </Grid>
          </FlexItem>
          <FlexItem>
            <Grid>
              <GridItem span={6}>
                <Text>{t('timezone')}</Text>
              </GridItem>
              <GridItem span={6}>
                <TypeaheadSelectInput
                  aria-label="Timezone"
                  emptyText={t('selectATimezone')}
                  value={formData.timeZone}
                  options={props.timezoneList}
                  optionToStringMap={(s) => s}
                  onChange={(tz) => setProperty({ timeZone: tz })}
                  autoSize={false}
                />
              </GridItem>
            </Grid>
          </FlexItem>
        </Flex>
      </Form>
    </Modal>
  );
}

export default connect(mapStateToProps, mapDispatchToProps)(NewTenantFormModal);
